import { useState, useEffect } from "react";
import { API, axios } from "@/api";
import { Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";

const SIGNAL_STYLES = {
  high_demand: { bg: "bg-red-100", text: "text-red-700", label: "High Demand", icon: TrendingUp },
  balanced: { bg: "bg-green-100", text: "text-green-700", label: "Balanced", icon: Minus },
  low_demand: { bg: "bg-blue-100", text: "text-blue-700", label: "Low Demand", icon: TrendingDown },
};

export default function CategoryDemandPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/category-demand`)
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  if (!data) return <p className="text-sm text-slate-400 text-center py-4">Failed to load</p>;

  const { total_views, categories } = data;
  const maxViews = categories.length > 0 ? categories[0].views : 1;

  return (
    <div className="space-y-3" data-testid="category-demand-panel">
      <div className="bg-gradient-to-r from-sky-50 to-indigo-50 rounded-xl p-3 border border-sky-100">
        <p className="text-xs text-sky-600 font-medium">Total Category Views</p>
        <p className="text-2xl font-bold text-sky-800">{total_views}</p>
        <p className="text-[10px] text-sky-500 mt-0.5">Demand/Supply ratio: high (&gt;5) = users want more, low (&lt;1) = oversupplied</p>
      </div>

      <div className="space-y-2">
        {categories.map((cat) => {
          const sig = SIGNAL_STYLES[cat.signal] || SIGNAL_STYLES.balanced;
          const SigIcon = sig.icon;
          return (
            <div key={cat.category} className="bg-white rounded-xl border border-slate-100 p-3" data-testid={`demand-cat-${cat.category}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-slate-800">{cat.category}</span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${sig.bg} ${sig.text} flex items-center gap-0.5`}>
                    <SigIcon className="w-3 h-3" />{sig.label}
                  </span>
                </div>
                <span className="text-sm font-bold text-slate-700">{cat.views}</span>
              </div>
              {/* Bar */}
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-2">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    cat.signal === "high_demand" ? "bg-red-400" :
                    cat.signal === "balanced" ? "bg-emerald-400" : "bg-sky-400"
                  }`}
                  style={{ width: `${(cat.views / maxViews) * 100}%` }}
                />
              </div>
              {/* Stats row */}
              <div className="flex items-center gap-3 text-[10px] text-slate-400">
                <span>{cat.pct_of_total}% of views</span>
                <span>{cat.unique_viewers} viewers</span>
                <span>{cat.unique_offers_viewed}/{cat.total_offers_in_category} offers viewed</span>
                <span className="font-semibold">D/S: {cat.demand_supply_ratio}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
